import React, { useState } from 'react';
import { Button, Grid } from '@mui/material';
import NavButtons, { NavButton } from 'shared/components/NavButtons';
import { RowSpaceBetween } from 'shared/components/styled';
import { useDeleteCustomPortal, useNewCustomPortalMutation, useUpdateCustomPortalMutation } from 'shared/apollo';
import { useSearch, queryToSearch } from 'shared/components/makeNav';
import Chip from '@mui/material/Chip';
import without from 'lodash/without';
import omit from 'lodash/omit';

import PropTypes from 'prop-types';
import TableIcon from '@mui/icons-material/GridOn';
import CardIcon from '@mui/icons-material/Dns';
import { makeStyles } from '@mui/styles';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import DropDownMenu from 'shared/components/DropDownMenu';
import NavValue from 'shared/components/NavValue';
import NavMenuItem from 'shared/components/NavMenuItem';
import GridExtra from 'shared/components/GridExtra';
import FilterDropDown from 'shared/components/FilterDropDown';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    top: 0,
  },
  rootGrid: {
    flexGrow: 1,
    margin: theme.spacing(1)
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
  },
  customButton: {
    position: 'absolute',
    right: '10px',
    bottom: '10px',
  },
  deleteButton: {
    marginRight: '10px'
  }
}));

export default function Grouping({ children, customPortal, rows, filters, extra, onFilterValue }) {
  const { push } = useHistory();
  const classes = useStyles();
  const screen = window.location.pathname.includes('custom') ? 'custom' : 'home';
  const { data: portal, refresh } = customPortal;
  const { run, isLoading } = useNewCustomPortalMutation();
  const { run: runUpdate, isLoading: isUpdating } = useUpdateCustomPortalMutation();
  const { run: runDelete, isLoading: isDeleting } = useDeleteCustomPortal();
  const { pathname } = useLocation();
  const query = useSearch();

  const saveCustomPortal = () => {
    const { pathname, search } = window.location;
    const url = pathname.replace(`/donor/${screen}/`, '') + search;
    run({
      name: 'Default',
      template: JSON.stringify({ url })
    }, refresh);
  };

  const updateCustomPortal = () => {
    const { pathname, search } = window.location;
    const url = pathname.replace(`/donor/${screen}/`, '') + search;
    runUpdate({
      id: portal.dbId,
      name: 'Default',
      template: JSON.stringify({ url })
    }, refresh);
  };

  const deleteCustomPortal = () => {
    runDelete({
      id: portal.dbId,
    }, () => {
      refresh();
      push('/donor/home');
    });
    
  };

  return (<>
    <RowSpaceBetween>
      {children}
    </RowSpaceBetween>
    <div className={classes.root}>
      <Grid
        container
        direction="column"
        xs={12}
        style={{flexDirection: 'row'}}
      >
        <GridExtra
          container
          alignItems="center"
          xs={12}
          spacing={1}
          padding={8}
        >
          {filters.length > 0 && <Grid item>
            <IndentedLabel>Selected Filters</IndentedLabel>
          </Grid>}
          <Grid item>
            {filters
              ?.map(({ filter, value }) => (
                <Chip
                  key={`${filter}=${value}`}
                  variant="outlined"
                  label={`${filterLabel[filter]}: ${value}`}
                  onDelete={() => {
                    const updated = removeFromSearch(query, filter, value);
                    push(`${pathname}?${updated}`);
                  }}
                />
              ))}
          </Grid>
        </GridExtra>
      </Grid>
      { !portal && rows && rows.length > 0 && (
        <Button
          disabled={isLoading}
          onClick={saveCustomPortal}
          className={classes.customButton}
        >Save Custom Portal</Button>
      )}
      { screen === 'custom' && portal && portal.dbId && (
        <CustomPortalButtons>
          <Button
            size="small"
            color="secondary"
            disabled={isUpdating || isDeleting}
            onClick={deleteCustomPortal}
            className={classes.deleteButton}
          >Delete Custom Portal</Button>
          <Button
            size="small"
            disabled={isUpdating || isDeleting}
            onClick={updateCustomPortal}
          >
            Save Custom Portal
          </Button>
        </CustomPortalButtons>
      )}
    </div>
  </>);
}

Grouping.propTypes = {
  children: PropTypes.node
};

const Label = styled.span`
  margin-right: 10px;
`;
const IndentedLabel = styled(Label)`
  margin-left: 70px !important;
`;

const ViewModeRoot = styled.div`
  display: flex;
  align-items: center;
`;

const CustomPortalButtons = styled.div`
  position: absolute;
  right: 10px;
  bottom: 10px;
`;

export const ViewModeToggle = () => {
  const screen = window.location.pathname.includes('custom') ? 'custom' : 'home';
  return (
    <ViewModeRoot>
      &nbsp;View as&nbsp;&nbsp;&nbsp;
      <NavButtons color="primary">
        <NavButton
          value={`/donor/${screen}/*/grid`}
          title="Table View"
        >
          <TableIcon/>
        </NavButton>
        <NavButton
          value={`/donor/${screen}/*/card`}
          title="Card View"
        >
          <CardIcon/>
        </NavButton>
      </NavButtons>
    </ViewModeRoot>);
};

const SelectableDropDownMenu = styled(DropDownMenu)`
  color: rgba(0,0,0,.38) !important;
  ${({ selected }) => selected && 'background-color: rgba(0,0,0,.2) !important;'}
  text-transform: none !important;
  border-left: 0 !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-color: #ddd !important;
`;

function removeFromSearch(query, filter, value) {
  const after = without(
    JSON.parse(query[filter]),
    value,
  );
  return after.length
    ? queryToSearch({ ...query, [filter]: encodeURIComponent(JSON.stringify(after)) })
    : queryToSearch(omit(query, filter));
}

const filterLabel = {
  npo: 'NPO',
  portfolio: 'Portfolio',
  manager: 'Manager',
  status: 'Status',
  stage: 'Stage',
  service: 'Service',
  'donation-size': 'Donation Size',
  year: 'Year',
  archive: 'Archive',
};

const amountCategories = [
  'Below $5K',
  'Below $10K',
  'Below $25K',
  'Below $100K',
  'Above $100K',
  'Below $250K',
  'Above $250K',
];
export const keyIdForAmountCategory5 = cat => amountCategories.indexOf(cat);
export const keyIdForAmountCategory10 = cat => amountCategories.indexOf(cat);

const statuses = [
  'Unnotified',
  'Sending',
  'Waiting',
  'Allocated',
  'Returned',
  'Rejected',
  'Application',
  'Scheduled',
  'Draft',
  'Awaiting TI Feedback',
  'TI Feedback Provided',
  'Awaiting TI Verification',
  'Awaiting Funder Feedback',
  'Funder Feedback Provided',
  'Awaiting Funder Verification',
  'Published',
  'Finalized',
]
  .map(s => s.toLowerCase());

export const keyIdForStatus = status => statuses.indexOf(status.toLowerCase());

const stages = [
  'Unallocated', 'Draft', 'Initial Report', 'Interim Report', 'Final Report',
]
  .map(s => s.toLowerCase());

export const keyIdForStage = stage => stages.indexOf(stage.toLowerCase());
